import { Inject, Injectable, NgZone, Optional, TemplateRef } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { Observable, Subject } from 'rxjs';
import { DialogPosition, MatDialog, MatDialogRef, MatDialogState, MatDialogConfig as _MatDialogConfig } from '@angular/material/dialog';

const diractionMap = { left: 'left', right: 'left', top: 'top', bottom: 'top' };
const multyMap = { left: 1, right: -1, top: 1, bottom: -1 };

export interface AnimationOption {
  keyframes?: Keyframe[];
  keyframeAnimationOptions: KeyframeAnimationOptions;
}

export interface DirectionMap {
  left: 'left';
  right: 'right';
  top: 'top';
  bottom: 'bottom'
}

export interface MatDialogConfig extends _MatDialogConfig {
  restoreFocus?: boolean;
  animation?:
  | {
    to: 'aside' | 'top' | 'bottom' | 'left' | 'right';
    incomingOptions?: { keyframes?: Keyframe[]; keyframeAnimationOptions: KeyframeAnimationOptions };
    outgoingOptions?: { keyframes?: Keyframe[]; keyframeAnimationOptions: KeyframeAnimationOptions };
  }
  | {
    to?: 'aside' | 'top' | 'bottom' | 'left' | 'right';
    incomingOptions?: { keyframes: Keyframe[]; keyframeAnimationOptions: KeyframeAnimationOptions };
    outgoingOptions?: { keyframes: Keyframe[]; keyframeAnimationOptions: KeyframeAnimationOptions };
  };
  position?: DialogPosition & { rowStart?: string; rowEnd?: string };
}

@Injectable({
  providedIn: 'root',
})
export class DialogAnimationService {
  // DEVEX2-1691: The animation does not work properly if a dialog is already open (e.g. we are opening a second dialog)
  // This leads to the dialogs not closing properly and the user potentially getting stuck with a half open invisible dialog.
  // The workaround for now is to block any animated dialogs from opening while we already have one opened.
  private _isAnimatedDialogOpen: boolean = false;

  constructor(
    private dialog: MatDialog,
    private ngZone: NgZone,
    @Optional()
    @Inject('INCOMING_OPTION')
    private incomingOptions?: AnimationOption,
    @Optional()
    @Inject('OUTGOING_OPTION')
    private outgoingOptions?: AnimationOption,
  ) { }

  private returnDummyRef<T, D = any, R = any>(): MatDialogRef<T, R> {
    return <MatDialogRef<T, R>>{
      close(result?: R): void {
      },
      afterOpened(): Observable<void> {
        return new Observable<void>(s => s.complete());
      },
      afterClosed(): Observable<R> {
        return new Observable<R>(s => s.complete());
      },
      beforeClosed(): Observable<R> {
        return new Observable<R>(s => s.complete());
      },
      backdropClick(): Observable<MouseEvent> {
        return new Observable<MouseEvent>(s => s.complete());
      },
      keydownEvents(): Observable<KeyboardEvent> {
        return new Observable<KeyboardEvent>(s => s.complete());
      },
      updatePosition(position?: DialogPosition): MatDialogRef<T, R> {
        return this;
      },
      updateSize(width?: string, height?: string): MatDialogRef<T, R> {
        return this;
      },
      addPanelClass(classes: string | string[]): MatDialogRef<T, R> {
        return this;
      },
      removePanelClass(classes: string | string[]): MatDialogRef<T, R> {
        return this;
      },
      getState() {
        return MatDialogState.CLOSED;
      }
    }
  }

  public open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig,
  ): MatDialogRef<T, R> {
    if (!config) {
      config = {};
    }

    if (config.animation && this._isAnimatedDialogOpen) {
      // We do not support this, return a dummy ref that does nothing
      // and cleans up any Observable subscriptions that get subscribed.
      console.error('Tried opening animated dialog while another one was already opened');
      return this.returnDummyRef();
    }

    const dir: 'ltr' | 'rtl' =
      config.direction || (document.querySelectorAll('[dir="rtl"]').length ? 'rtl' : 'ltr');
    config.direction = config.direction || dir;
    if (config.animation) {
      if (config.animation.to === 'aside') {
        config.animation.to = dir === 'rtl' ? 'left' : 'right';
      }
    }

    if (config.position && config.position.rowEnd) {
      if (dir === 'rtl') {
        config.position.right = config.position.rowEnd;
      } else {
        config.position.left = config.position.rowEnd;
      }
    }

    if (config.position && config.position.rowStart) {
      if (dir === 'rtl') {
        config.position.left = config.position.rowStart;
      } else {
        config.position.right = config.position.rowStart;
      }
    }

    config.restoreFocus = config.restoreFocus ?? false;

    const ref = this.dialog.open(componentOrTemplateRef, config);
    this._isAnimatedDialogOpen = !!config.animation;

    if (config.animation) {
      const incomingOptions: AnimationOption = config.animation.incomingOptions ||
        this.incomingOptions || { keyframeAnimationOptions: { duration: 300, easing: 'ease-in' } };

      const outgoingOptions: AnimationOption = config.animation.outgoingOptions ||
        this.outgoingOptions || { keyframeAnimationOptions: { duration: 300, easing: 'ease-out' } };

      // Use our own wrapper if we are using full screen dialogs since it uses position:fixed, otherwise the animation doesn't work
      const fullScreenDialog = document.querySelector('.my-full-screen-dialog .mat-mdc-dialog-container');
      const wrapper = fullScreenDialog ?? document.getElementsByClassName('cdk-global-overlay-wrapper')[0];

      const animate = (keyframes: Keyframe[], options: KeyframeAnimationOptions) => {
        return wrapper.animate(keyframes, options);
      };

      const _afterClosed = new Subject();
      ref.afterClosed = () => {
        return _afterClosed.asObservable();
      };
      const closeFunction = ref.close;

      let incomeKeyFrames: Keyframe[] | undefined = incomingOptions.keyframes;
      let outgoingKeyFrames: Keyframe[] | undefined = outgoingOptions.keyframes;
      if (config.animation.to) {
        const container = document.getElementsByTagName('mat-dialog-container')[0] as HTMLElement;
        const to: string = diractionMap[config.animation.to as keyof DirectionMap];
        const keyFrame100: Keyframe = {};
        const keyFrame0: Keyframe = {};
        keyFrame0[to] = 0;
        keyFrame100[to] =
          to === 'top' || to === 'bottom'
            ? container.clientHeight * multyMap[config.animation.to as keyof DirectionMap] + 'px'
            : container.clientWidth * multyMap[config.animation.to as keyof DirectionMap] + 'px';
        incomeKeyFrames = incomeKeyFrames || [keyFrame100, keyFrame0];
        outgoingKeyFrames = outgoingKeyFrames || [keyFrame0, keyFrame100];
      }

      // Play the incoming animation
      if (incomeKeyFrames) {
        animate(incomeKeyFrames, incomingOptions.keyframeAnimationOptions);
      }

      const closeHandler = (dialogResult?: R) => {
        try {
          // Make sure that afterClosed is always called from the NgZone
          this.ngZone.run(() => {
            _afterClosed.next(dialogResult);
          });
        }
        finally {
          const onAnimationFinished = () => {
            this.ngZone.run(() => {
              // Call the original close function that we have overridden
              closeFunction.bind(ref)(dialogResult);
              // Hide the wrapper to prevent flickering when the animation finishes
              (wrapper as HTMLElement).style.display = 'none';
              // Dialog is closed now
              this._isAnimatedDialogOpen = false;
            });
          }
          // Play the outgoing animation
          if (outgoingKeyFrames) {
            const animation = animate(outgoingKeyFrames, outgoingOptions.keyframeAnimationOptions);
            animation.onfinish = onAnimationFinished;
          }
          else {
            onAnimationFinished();
          }

          // Restore the original close function to prevent this handler from being invoked again
          ref.close = closeFunction;
        }
      };
      ref.close = (dialogResult?: R) => closeHandler(dialogResult);
    }

    return ref;
  }
}
